import { Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { Style } from '@react-pdf/types';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import defaultTheme from 'theme/defaultTheme';
import { formattedDate } from 'utils/dates';
// import { getImgUrl } from 'utils/epi';
import PdfPageTitle from '../Components/PdfPageTitle';
import { Slant } from '../Components/Shapes';
import ExportPdfContext from '../ExportPdfContext';
import { PdfPageInfo } from '../exportPdfTypes';

export const styles = StyleSheet.create({
    page: {
        fontFamily: 'PDF Font',
        paddingBottom: 65,
        paddingHorizontal: 16,
        paddingTop: 16,
    },
    fill: {
        backgroundColor: defaultTheme.colors.fill,
    },
    footer: {
        fontSize: 9,
        position: 'absolute',
        display: 'flex',
        flexDirection: 'row',
        bottom: 0,
        left: 0,
        right: 0,
        alignItems: 'center',
        color: defaultTheme.colors.text,
        justifyContent: 'space-between',
    },
    header: {
        bottom: 'auto',
        top: 0,
        padding: 8,
        justifyContent: 'center',
    },
    footerLeft: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'row',
        paddingTop: 10,
        paddingBottom: 4,
        paddingLeft: 12,
    },
    footerRight: {
        paddingBottom: 4,
        paddingTop: 10,
        paddingRight: 12,
    },
    footerText: {
        display: 'flex',
        flexDirection: 'row',
        lineHeight: 2,
    },
    footerLogo: {
        maxWidth: 60,
        maxHeight: 25,
        marginRight: 8,
    },
    footerDivider: {
        paddingLeft: 6,
        paddingRight: 6,
    },
    footerBackgroundWrapper: {
        position: 'absolute',
        display: 'flex',
        top: '-5px',
        left: 0,
        width: '100%',
        height: '65px',
    },
    footerBackground: {
        height: '40px',
    },
});

export type PageWithFooterProps = PdfPageInfo & {
    children?: React.ReactNode;
    style?: Style;
    wrap?: boolean;
    fill?: boolean;
    header?: string;
    headerText?: string;
    useLandscape?: boolean;
};

export const PdfPageWithFooter = ({
    seller,
    children,
    style,
    wrap,
    fill,
    orderNumber,
    header,
    headerText,
    useLandscape,
}: PageWithFooterProps) => {
    const { t } = useTranslation();
    const exportPdfContext = useContext(ExportPdfContext);
    const orientation = useLandscape ? 'landscape' : exportPdfContext.orientation;
    const pageStartingNumber = exportPdfContext.pageStartingNumber;
    const pageStyles: Style[] = [styles.page];
    if (fill) pageStyles.push(styles.fill);
    if (style) pageStyles.push(style);
    if (headerText) {
        // Approximation of how much top space the header-text needs so it does not overlap with title/content
        const lettersPerRow = orientation === 'landscape' ? 200 : 150;
        pageStyles.push({
            paddingTop: Math.ceil(headerText.length / lettersPerRow) * 12 + 24,
        });
    }

    return (
        <Page wrap={wrap ?? true} style={pageStyles} size="A4" orientation={orientation}>
            {headerText && (
                <View
                    fixed
                    style={[
                        styles.footer,
                        styles.header,
                        { backgroundColor: fill ? defaultTheme.colors.fillMedium : defaultTheme.colors.fill },
                    ]}
                >
                    <Text>{headerText}</Text>
                </View>
            )}
            {header && <PdfPageTitle>{header}</PdfPageTitle>}
            {children}
            {
                <View style={styles.footer} fixed>
                    <View style={styles.footerBackgroundWrapper}>
                        <Slant color={fill ? defaultTheme.colors.fillMedium : undefined} style={{ height: '30%' }} />
                        <View
                            style={[
                                styles.footerBackground,
                                {
                                    backgroundColor: fill ? defaultTheme.colors.fillMedium : defaultTheme.colors.fill,
                                },
                            ]}
                        ></View>
                    </View>
                    <View style={styles.footerLeft}>
                        {seller?.display && (
                            <>
                                {seller.imageUrl?.length ? (
                                    <View style={styles.footerText}>
                                        {/* <Image cache={true} style={styles.footerLogo} src={getImgUrl(seller.imageUrl)} /> */}
                                    </View>
                                ) : null}
                                {seller.company ? (
                                    <View style={styles.footerText}>
                                        <Text>{seller.company}</Text>
                                        {seller.name || seller.phone || seller.email ? (
                                            <Text style={styles.footerDivider}>|</Text>
                                        ) : null}
                                    </View>
                                ) : null}
                                {seller.name ? (
                                    <View style={styles.footerText}>
                                        <Text>{seller.name}</Text>
                                        <Text style={styles.footerDivider}>|</Text>
                                    </View>
                                ) : null}
                                {seller.phone ? (
                                    <View style={styles.footerText}>
                                        <Text>{seller.phone}</Text>
                                        <Text style={styles.footerDivider}>|</Text>
                                    </View>
                                ) : null}
                                {seller.email ? (
                                    <View style={styles.footerText}>
                                        <Text>{seller.email}</Text>
                                        <Text style={styles.footerDivider}>|</Text>
                                    </View>
                                ) : null}
                            </>
                        )}
                        {seller?.display || orderNumber ? (
                            <View style={styles.footerText}>
                                <Text>{formattedDate(new Date())}</Text>
                            </View>
                        ) : null}
                        {orderNumber && (
                            <View style={styles.footerText}>
                                <Text style={styles.footerDivider}>|</Text>
                                <Text>
                                    {t('order.orderNumber')}: {orderNumber}
                                </Text>
                            </View>
                        )}
                    </View>
                    <View style={styles.footerRight}>
                        <Text
                            render={({ pageNumber, totalPages }) => {
                                if (totalPages < 2) return '';
                                return `${t('navigation.page')} ${
                                    typeof pageStartingNumber === 'number'
                                        ? pageNumber + pageStartingNumber
                                        : `${pageNumber} / ${totalPages}`
                                }`;
                            }}
                        />
                    </View>
                </View>
            }
        </Page>
    );
};
